import React, { useRef, useState } from "react";
import HeroSection from "./components/HeroSection";
import ProductLinkPricing from "./components/ProductLinkPricing";
import PricingTool from "./components/PricingTool";
import InquiryForm from "./components/InquiryForm";
import QuotationPreview from "./components/QuotationPreview";
import TermsSection from "./components/TermsSection";
import { useCart } from "./hooks/useCart";
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

/**
 * HardwarePricingPage
 * The main page for the hardware pricing feature.
 * Drop this into your React Router as:
 *   <Route path="/hardware-pricing" element={<HardwarePricingPage />} />
 *
 * File lives at: src/pages/hardware-pricing/index.jsx
 * (or wherever your pages folder is)
 */
const HardwarePricingPage = () => {
  const toolRef = useRef(null);
  const cartRef = useRef(null);
  const formRef = useRef(null);
  const previewRef = useRef(null);

  const [quoteData, setQuoteData] = useState(null);

  const {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateDelivery,
    subtotal,
    gstAmount,
    totalWithGST,
    itemCount,
  } = useCart();

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAddToCart = (product, qty, deliveryType) => {
    addToCart(product, qty, deliveryType);
    // Small delay so user sees the "Added!" state, then scroll down to cart
    setTimeout(() => scrollTo(cartRef), 600);
  };

  const handleQuoteReady = (data) => {
    setQuoteData(data);
    setTimeout(() => scrollTo(previewRef), 400);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 1. Hero */}
      <Header />
      <HeroSection onScrollToTool={() => scrollTo(toolRef)} />

      {/* 2. URL pricing tool */}
      <div ref={toolRef}>
        <ProductLinkPricing onAddToCart={handleAddToCart} />
      </div>

      {/* 3. Cart + live pricing summary */}
      <div ref={cartRef}>
        <PricingTool
          cartItems={cartItems}
          onRemove={removeFromCart}
          onUpdateQty={updateQuantity}
          onUpdateDel={updateDelivery}
          subtotal={subtotal}
          gstAmount={gstAmount}
          totalWithGST={totalWithGST}
          onProceed={() => scrollTo(formRef)}
        />
      </div>

      {/* 4. Inquiry / quotation form */}
      <div ref={formRef}>
        <InquiryForm
          cartItems={cartItems}
          subtotal={subtotal}
          gstAmount={gstAmount}
          totalWithGST={totalWithGST}
          onQuoteReady={handleQuoteReady}
        />
      </div>

      {/* 5. Quotation preview (shown after form submit) */}
      {quoteData && (
        <div ref={previewRef}>
          <QuotationPreview quoteData={quoteData} />
        </div>
      )}

      {/* 6. Terms */}
      <TermsSection />
      <Footer />
    </div>
  );
};

export default HardwarePricingPage;